import type {
  ReportCatalog,
  ReportCompare,
  ReportPeriodPreset,
  ReportQuery,
  ReportRecordsRequest,
  ReportTile
} from "@gettactic/api";

const COMPARE_LABELS: Record<ReportCompare, { long: string; short: string }> = {
  previous_period: { long: "the previous period", short: "vs previous" },
  same_period_last_year: {
    long: "the same period last year",
    short: "vs last year"
  }
};

/** "the previous period" / "the same period last year". */
export const compareLabel = (compare: ReportCompare): string =>
  COMPARE_LABELS[compare].long;

/** The delta caption: "vs previous" / "vs last year". */
export const compareShortLabel = (
  compare: ReportCompare | undefined
): string => (compare ? COMPARE_LABELS[compare].short : "vs previous");

const PERIOD_LABELS: Record<ReportPeriodPreset, string> = {
  last_7_days: "last 7 days",
  last_30_days: "last 30 days",
  last_90_days: "last 90 days",
  year_to_date: "year to date",
  custom: "custom period"
};

export const periodLabel = (period: ReportQuery["period"]): string => {
  if (period.preset === "custom" && period.from && period.to) {
    return `${period.from} to ${period.to}`;
  }
  return PERIOD_LABELS[period.preset];
};

const memberLabel = (
  catalog: ReportCatalog,
  dimension: string,
  id: string
): string =>
  catalog.dimensions
    .find((d) => d.key === dimension)
    ?.members.find((m) => m.id === id)?.label ?? id;

const measureLabel = (catalog: ReportCatalog, key: string): string =>
  catalog.measures.find((m) => m.key === key)?.label ?? key;

/** "Requests filed" as it reads mid-sentence: "requests filed". */
export const lowerFirst = (s: string): string =>
  s.charAt(0).toLowerCase() + s.slice(1);

/**
 * One readable sentence for a query, used as the default tile title and as
 * the "you asked" line: "Requests filed by category in Lisbon HQ, last 30
 * days, compared to the previous period". A paired measure reads "Requests
 * filed vs requests resolved".
 */
export const describeQuery = (
  catalog: ReportCatalog,
  query: ReportQuery,
  pairedWith?: string
): string => {
  const measure = measureLabel(catalog, query.measure);
  const parts = [
    pairedWith
      ? `${measure} vs ${lowerFirst(measureLabel(catalog, pairedWith))}`
      : measure
  ];

  if (query.breakdown?.length) {
    const labels = query.breakdown.map(
      (key) => catalog.dimensions.find((d) => d.key === key)?.label ?? key
    );
    parts.push(`by ${labels.join(" and ").toLowerCase()}`);
  }

  for (const filter of query.filters ?? []) {
    const labels = filter.in.map((id) =>
      memberLabel(catalog, filter.dimension, id)
    );
    parts.push(`in ${labels.join(", ")}`);
  }

  let sentence = `${parts.join(" ")}, ${periodLabel(query.period)}`;
  if (query.compare) {
    sentence += `, compared to ${compareLabel(query.compare)}`;
  }
  return sentence;
};

/** "Reservations in Lisbon HQ, last 30 days" for a dataset selection. */
export const describeRecords = (
  catalog: ReportCatalog,
  request: ReportRecordsRequest
): string => {
  const dataset = catalog.datasets.find((d) => d.key === request.dataset);
  const parts = [dataset?.label ?? request.dataset];
  for (const f of request.filters ?? []) {
    const labels = f.in.map((id) => memberLabel(catalog, f.dimension, id));
    parts.push(
      `${f.dimension === "office" ? "in" : "for"} ${labels.join(", ")}`
    );
  }
  if (request.period && !dataset?.timeless) {
    parts.push(periodLabel(request.period));
  }
  if (request.time_from && request.time_to) {
    parts.push(`between ${request.time_from} and ${request.time_to}`);
  }
  return parts.join(", ");
};

/** A tile's fallback title, whichever kind it is. */
export const describeTile = (
  catalog: ReportCatalog,
  tile: ReportTile
): string =>
  tile.records
    ? describeRecords(catalog, tile.records)
    : tile.query
      ? describeQuery(catalog, tile.query, tile.chart?.with)
      : tile.title;
